import type { PermitFeesTotals } from '@contracts/order/ReviewableOrderResponse';
import { roundToTwoDecimalsString } from '@contracts/utils/RoundingAmount';
import TrailEnd from '@src/common/assets/trail-end.svg?react';
import TrailLine from '@src/common/assets/trail-line.svg?react';
import TrailMiddle from '@src/common/assets/trail-middle.svg?react';
import TrailStart from '@src/common/assets/trail-start.svg?react';
import { Button } from '@src/common/components/button/Button';
import { MoneyInput } from '@src/common/components/MoneyInput';
import { Stack } from '@src/common/components/Stack';
import type {
  ColumnContent,
  ColumnHeader,
} from '@src/common/components/TableWithSubRows';
import { TableWithSubRows } from '@src/common/components/TableWithSubRows';
import { TextInput } from '@src/common/components/TextInput';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { PermitStatus } from '@src/orders/domain/PermitApplication';
import type {
  ReviewableOrder,
  ReviewablePermitApplication,
  SubsequentReviewablePermitApplication,
} from '@src/orders/domain/ReviewableOrder';
import { FormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import { PermitNotesButton } from '@src/orders/review/PermitNotesButton';
import { PermitTotal } from '@src/orders/review/PermitTotal';
import type {
  FeesFormFields,
  PermitFeeField,
} from '@src/orders/schemas/ReviewOrderFormPermitFields';
import {
  IsWaivedField,
  PermitInvoiceNumberField,
  PermitJurisdictionFeeField,
  PermitServiceFeeField,
} from '@src/orders/schemas/ReviewOrderFormPermitFields';
import { useState } from 'react';
import type { Control, Path, UseFormWatch } from 'react-hook-form';
import { useFormState, useWatch } from 'react-hook-form';
import type { UseFormRegister } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';

import { FillAllInvoiceNumberModal } from './FillAllInvoiceNumberModal';
import { FillAllServiceFeeModal } from './FillAllServiceFeeModal';

interface Props<T extends FeesFormFields> {
  order: ReviewableOrder;
  register: UseFormRegister<T>;
  control: Control<T>;
  watch: UseFormWatch<FeesFormFields>;
  totals: PermitFeesTotals;
  readOnly?: boolean;
  onFeesChanged?: () => void;
  setAllInvoiceNumber: (invoiceNumber: string, order: ReviewableOrder) => void;
  setAllServiceFee: (amount: string, order: ReviewableOrder) => void;
}

export const OrderPermitsFees = <T extends FeesFormFields>({
  order,
  register,
  control,
  totals,
  readOnly = false,
  onFeesChanged,
  setAllInvoiceNumber,
  setAllServiceFee,
}: Props<T>) => {
  const { t } = useTranslation();
  const { errors } = useFormState({ control });
  const values = useWatch({ control });

  const [openFillAllInvoiceNumberModal, setOpenFillAllInvoiceNumberModal] =
    useState(false);
  const [openFillAllServiceFeeModal, setOpenFillAllServiceFeeModal] =
    useState(false);

  const invoiceNumberReadOnly =
    readOnly || order.isCompleted() || order.isArchived();

  const columns: ColumnHeader[] = [
    { header: t('reviewPermit.jurisdiction') },
    { header: t('reviewPermit.name') },
    { header: t('reviewPermit.id'), showRightBorder: true },
    { header: t('reviewPermit.currency') },
    { header: t('reviewPermit.stateFees') },
    { header: '', minWidth: '120px' },
    { header: t('reviewPermit.waive'), showRightBorder: true },
    { header: t('reviewPermit.currency') },
    {
      header: (
        <HeaderWithSetAll
          title={t('reviewPermit.serviceFees')}
          onSetAll={() => setOpenFillAllServiceFeeModal(true)}
          canApplySetAll={!readOnly}
        />
      ),
    },
    { header: t('reviewPermit.waive'), showRightBorder: true },
    {
      header: (
        <HeaderWithSetAll
          title={t('reviewPermit.invoiceNumber')}
          onSetAll={() => setOpenFillAllInvoiceNumberModal(true)}
          canApplySetAll={!invoiceNumberReadOnly}
        />
      ),
      minWidth: '200px',
    },
    { header: t('reviewPermit.note') },
  ];

  const getFeeInput = ({
    field,
    permitId,
  }: {
    field: typeof PermitFeeField;
    permitId: string;
  }) => {
    const isWaived =
      values.permitFees?.[permitId]?.[field.fieldName]?.[
        IsWaivedField.fieldName
      ];
    return (
      <div className={feesInput}>
        <MoneyInput
          disabled={readOnly || isWaived}
          register={register}
          errors={errors}
          fieldName={
            `permitFees.${permitId}.${field.fieldName}.amount` as Path<T>
          }
          label={t(`reviewPermit.${field.fieldName}-aria`, { permitId })}
          onBlur={onFeesChanged}
        />
      </div>
    );
  };

  const getWaiveFeeCheckbox = ({
    field,
    permitId,
  }: {
    field: typeof PermitFeeField;
    permitId: string;
  }) => (
    <div className={waiveFee}>
      <FormCheckbox
        register={register}
        fieldName={
          `permitFees.${permitId}.${field.fieldName}.isWaived` as Path<T>
        }
        hideLabel
        readOnly={readOnly}
        className={checkbox}
        defaultChecked={
          values.permitFees?.[permitId]?.[field.fieldName]?.[
            IsWaivedField.fieldName
          ]
        }
        onChange={onFeesChanged}
      />
    </div>
  );

  const getInvoiceNumberInput = ({
    field,
    permit,
    disabled,
  }: {
    field: typeof PermitInvoiceNumberField;
    permit: ReviewablePermitApplication;
    disabled: boolean;
  }) => (
    <TextInput
      label="#"
      size="small"
      disabled={readOnly || disabled}
      defaultValue={permit.fees.invoiceNumber}
      {...register(`permitFees.${permit.id}.${field.fieldName}` as Path<T>)}
      onBlur={onFeesChanged}
      alwaysShrinkLabel={!!permit.fees.invoiceNumber}
      error={
        errors.permitFees &&
        //oxlint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        errors.permitFees[permit.id] &&
        //oxlint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        errors.permitFees[permit.id][field.fieldName] &&
        t(
          //oxlint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          `formErrors.${errors.permitFees[permit.id][field.fieldName]!.message}`,
        )
      }
    />
  );

  const getOriginalPermitColumnsContent = (
    permit: ReviewablePermitApplication,
    hasSubsequentPermits: boolean,
  ): ColumnContent[] => {
    return [
      { content: permit.jurisdiction },
      {
        content: (
          <OriginalPermitName
            name={permit.name}
            hasSubsequentPermits={hasSubsequentPermits}
            isCanceled={permit.status === PermitStatus.CANCELLED}
          />
        ),
      },
      ...getCommonColumnsContent(permit),
    ];
  };

  const getSubsequentPermitColumnsContent = (
    permit: SubsequentReviewablePermitApplication,
    isLastSubsequent: boolean,
  ): ColumnContent[] => {
    return [
      { content: permit.jurisdiction },
      {
        content: (
          <SubsequentPermitName
            name={permit.name}
            trigger={permit.trigger}
            isCanceled={permit.status === PermitStatus.CANCELLED}
            isLastSubsequent={isLastSubsequent}
          />
        ),
      },
      ...getCommonColumnsContent(permit),
    ];
  };

  const getFeeConversion = (permit: ReviewablePermitApplication): string => {
    if (showFeeConversion(permit)) {
      return (
        permit.fees.jurisdictionFee.convertedFee.currency +
        ' $' +
        roundToTwoDecimalsString(
          permit.fees.jurisdictionFee.convertedFee.amount,
        )
      );
    } else {
      return '';
    }
  };

  const showFeeConversion = (permit: ReviewablePermitApplication): boolean => {
    return (
      order.billingCurrency != 'ALL' && !order.isSameCurrency(permit.currency)
    );
  };

  const getCommonColumnsContent = (permit: ReviewablePermitApplication) => {
    return [
      { content: permit.permitNumber, showRightBorder: true },
      { content: permit.fees.jurisdictionFee.currency },
      {
        content: getFeeInput({
          field: PermitJurisdictionFeeField,
          permitId: permit.id,
        }),
      },
      { content: getFeeConversion(permit) },
      {
        content: getWaiveFeeCheckbox({
          field: PermitJurisdictionFeeField,
          permitId: permit.id,
        }),
        showRightBorder: true,
      },
      {
        content: permit.fees.serviceFee.currency,
      },
      {
        content: getFeeInput({
          field: PermitServiceFeeField,
          permitId: permit.id,
        }),
      },
      {
        content: getWaiveFeeCheckbox({
          field: PermitServiceFeeField,
          permitId: permit.id,
        }),
        showRightBorder: true,
      },
      {
        content: getInvoiceNumberInput({
          field: PermitInvoiceNumberField,
          permit,
          disabled:
            order.isInvoiced() ||
            order.isModifyingInvoice() ||
            (!order.isInvoicing() && !order.isCompleted()) ||
            invoiceNumberReadOnly,
        }),
      },
      {
        content: (
          <PermitNotesButton
            permitId={permit.id}
            mode="reviewPermitNotes"
            disabled={readOnly}
          />
        ),
      },
    ];
  };

  const rowsContent = order.permitApplicationGroups.map((group) => {
    const originalPermit = group.originalPermit;
    const hasSubsequentPermits = group.subsequentPermits.length > 0;
    return {
      id: originalPermit.id,
      columnsContent: getOriginalPermitColumnsContent(
        originalPermit,
        hasSubsequentPermits,
      ),
      subRows: group.subsequentPermits.map((subsequentPermit, i) => ({
        id: subsequentPermit.id,
        columnsContent: getSubsequentPermitColumnsContent(
          subsequentPermit,
          i === group.subsequentPermits.length - 1,
        ),
      })),
    };
  });

  return (
    <Stack>
      <Typography variant="subtitle1" style={{ padding: theme.spacing['2'] }}>
        {t('reviewPermit.permitFees')}
      </Typography>

      <TableWithSubRows
        columnsName={columns}
        rows={rowsContent}
        ariaLabelledBy={t('reviewablePermits')}
      />

      <PermitTotal totals={totals} />
      <FillAllInvoiceNumberModal
        open={openFillAllInvoiceNumberModal}
        onClose={() => setOpenFillAllInvoiceNumberModal(false)}
        onSubmit={(invoiceNumber) => {
          setAllInvoiceNumber(invoiceNumber, order);
          if (onFeesChanged) {
            onFeesChanged();
          }
        }}
      />
      <FillAllServiceFeeModal
        open={openFillAllServiceFeeModal}
        onClose={() => setOpenFillAllServiceFeeModal(false)}
        onSubmit={(amount) => {
          setAllServiceFee(amount, order);
          if (onFeesChanged) {
            onFeesChanged();
          }
        }}
      />
    </Stack>
  );
};

type OriginalPermitNameProps = {
  name: string;
  hasSubsequentPermits: boolean;
  isCanceled: boolean;
};

const OriginalPermitName = ({
  name,
  hasSubsequentPermits,
  isCanceled,
}: OriginalPermitNameProps) => (
  <Stack direction="row" alignItems="center" gap={theme.spacing[1]}>
    {hasSubsequentPermits && (
      <>
        <TrailStart className={trailStart} />
        <TrailLine className={trailLine} />
      </>
    )}
    {isCanceled && <CancelSymbol />}
    {name}
  </Stack>
);

type SubsequentPermitNameProps = {
  name: string;
  isLastSubsequent: boolean;
  trigger: string;
  isCanceled: boolean;
};

const SubsequentPermitName = ({
  name,
  trigger,
  isLastSubsequent,
  isCanceled,
}: SubsequentPermitNameProps) => (
  <Stack direction="row" alignItems="center" gap={theme.spacing[1]}>
    {isLastSubsequent ? (
      <TrailEnd className={trailEnd} />
    ) : (
      <>
        <TrailMiddle className={trailMiddle} />
        <TrailLine className={trailLine} />
      </>
    )}
    {isCanceled && <CancelSymbol />}
    <Typography style={{ fontWeight: 500 }}>({trigger[0]})</Typography>
    {name}
  </Stack>
);

const CancelSymbol = () => (
  <Typography style={{ fontWeight: 500 }}>(C)</Typography>
);

type SetAllHeaderProps = {
  title: string;
  onSetAll: () => void;
  canApplySetAll: boolean;
};

const HeaderWithSetAll = ({
  title,
  onSetAll,
  canApplySetAll,
}: SetAllHeaderProps) => {
  const { t } = useTranslation();
  return (
    <Stack direction="column" alignItems="center">
      <Typography>{title}</Typography>
      {canApplySetAll && (
        <Button type="button" variant="text" onClick={onSetAll}>
          <Typography color={theme.palette.secondary.secondary}>
            {t('setAll')}
          </Typography>
        </Button>
      )}
    </Stack>
  );
};

const trailStart = css({
  position: 'relative',
  top: 14,
  left: 0,
});

const trailMiddle = css({
  position: 'relative',
  top: -8,
  left: 0,
});

const trailLine = css({
  position: 'relative',
  top: 20,
  left: -14,
});

const trailEnd = css({
  position: 'relative',
  top: -11,
  left: 0,
});

const feesInput = css({
  width: '116px',
});

const waiveFee = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const checkbox = css({
  flex: 1,
});
